import { Shield, Database, KeyRound, XCircle, CheckCircle, ShieldCheck, TrendingUp } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useDashboardStats } from "@/hooks/useDashboard";
import { Sparkline } from "@/components/charts/Sparkline";
import { useUsage } from "@/hooks/useBilling";
import { PlanBadge } from "@/components/billing/PlanBadge";
import { UsageMeter } from "@/components/billing/UsageMeter";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { KPICard } from "@/components/shared/KPICard";
import { PageHeader } from "@/components/shared/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SetupChecklist } from "@/components/onboarding/SetupChecklist";
import { formatRelativeDate } from "@/lib/formatters";
import { normalizePlanTier } from "@/types/billing";
import { useQuery } from "@tanstack/react-query";
import { getActivationStats } from "@/api/dashboard";

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const { data: stats, isLoading } = useDashboardStats();
  const { data: usage } = useUsage();
  const { data: activation } = useQuery({
    queryKey: ["dashboard", "activation"],
    queryFn: getActivationStats,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const deniedPct = stats && stats.retrievals_today > 0
    ? ((stats.retrievals_denied / stats.retrievals_today) * 100).toFixed(1)
    : "0";

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        eyebrow={`Welcome back, ${user?.name}`}
        title="Operations"
        actions={
          user?.organization?.plan ? (
            <PlanBadge tier={normalizePlanTier(user.organization.plan)} />
          ) : undefined
        }
        subtitle={user?.organization?.name}
      />

      <SetupChecklist />

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          icon={<Shield className="h-4 w-4" />}
          label="Retrieval Activity"
          value={stats?.retrievals_today.toLocaleString() ?? 0}
          accent="primary"
          sparkline={
            stats?.sparklines?.retrievals_24h?.length
              ? <Sparkline data={stats.sparklines.retrievals_24h} />
              : undefined
          }
          footer={
            <div className="flex gap-4">
              <span className="flex items-center gap-1 text-status-allow">
                <CheckCircle className="h-3 w-3" />
                {stats?.retrievals_allowed.toLocaleString()} allowed
              </span>
              <span className="flex items-center gap-1 text-status-deny">
                <XCircle className="h-3 w-3" />
                {stats?.retrievals_denied} denied ({deniedPct}%)
              </span>
            </div>
          }
        />

        <KPICard
          icon={<Database className="h-4 w-4" />}
          label="Connector Status"
          value={stats?.connectors_connected ?? 0}
          accent={stats?.connectors_error ? "warn" : "allow"}
          footer={
            <div className="flex items-center gap-2">
              <StatusBadge status="connected" />
              {(stats?.connectors_error ?? 0) > 0 && (
                <span className="text-status-warning">{stats?.connectors_error} with errors</span>
              )}
            </div>
          }
        />

        <KPICard
          icon={<KeyRound className="h-4 w-4" />}
          label="Identity Providers"
          value={stats?.idps_principal_count.toLocaleString() ?? 0}
          sparkline={
            stats?.sparklines?.principals_7d?.length
              ? <Sparkline data={stats.sparklines.principals_7d} />
              : undefined
          }
          footer={
            <>
              {stats?.idps_connected} provider{stats?.idps_connected !== 1 ? "s" : ""} connected
              {stats?.last_idp_sync && <> · Last sync {formatRelativeDate(stats.last_idp_sync)}</>}
            </>
          }
        />

        <KPICard
          icon={<ShieldCheck className="h-4 w-4" />}
          label="Policy Coverage"
          value={stats?.overall_coverage_pct != null ? `~${stats.overall_coverage_pct}%` : "—"}
          accent={
            stats?.overall_coverage_pct != null
              ? stats.overall_coverage_pct >= 80 ? "allow" : "warn"
              : undefined
          }
          sparkline={
            stats?.sparklines?.coverage_7d?.length
              ? <Sparkline data={stats.sparklines.coverage_7d} color="hsl(var(--status-allow))" />
              : undefined
          }
          footer={
            <>
              {stats?.total_bound_vectors.toLocaleString()} of{" "}
              {stats?.total_vectors.toLocaleString()} vectors bound
            </>
          }
        />
      </div>

      {/* Usage Meter */}
      {usage && (
        <Card className="shadow-elevation">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Usage This Period</CardTitle>
          </CardHeader>
          <CardContent>
            <UsageMeter
              used={usage.secured_retrievals.used}
              limit={usage.secured_retrievals.limit}
              label="Secured Retrievals"
            />
          </CardContent>
        </Card>
      )}

      {/* Activation Metrics (internal ops view) */}
      {activation && (
        <Card className="shadow-elevation">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Activation Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">30-day retrievals</p>
                <p className="font-semibold tabular">{activation.total_retrievals_30d.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  {activation.allowed_retrievals_30d.toLocaleString()} allowed ·{" "}
                  <span className="text-status-deny">{activation.denied_retrievals_30d.toLocaleString()} denied</span>
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">No-access retrievals</p>
                <p className="font-semibold tabular">{activation.no_access_retrievals_30d.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">0 results authorized</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">p95 latency</p>
                <p className="font-semibold tabular">
                  {activation.p95_latency_ms != null ? `${Math.round(activation.p95_latency_ms)}ms` : "—"}
                </p>
                <p className="text-xs text-muted-foreground">end-to-end</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Days to first policy</p>
                <p className="font-semibold tabular">
                  {activation.days_to_first_active_policy != null ? `${activation.days_to_first_active_policy}d` : "—"}
                </p>
                <p className="text-xs text-muted-foreground">from signup</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Denied Retrievals */}
      <div>
        <h2 className="text-base font-semibold mb-4 text-foreground">Recent Denied Retrievals</h2>
        {stats?.recent_denied && stats.recent_denied.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border shadow-elevation">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Query</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Principal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Denial reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Time</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_denied.map((item) => (
                  <tr key={item.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                    <td className="px-4 py-3 max-w-xs truncate font-medium">{item.query}</td>
                    <td className="px-4 py-3 text-muted-foreground">{item.principal_name}</td>
                    <td className="px-4 py-3 text-xs text-status-deny max-w-sm truncate">{item.denial_reason}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap tabular">
                      {formatRelativeDate(item.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No denied retrievals recently.</p>
        )}
      </div>
    </div>
  );
}
